import type { Screening } from "@/types/screeners";

import { GuideBlock } from "./interview-guide-block";

interface InterviewGuideProps {
  screening: Screening;
}

const MAX_GUIDE_ITEMS = 5;

/**
 * Normalizes interview guide list items returned by the API/worker.
 * Expected input is an array of sentence-like strings (already list-split),
 * where entries can include extra whitespace or terminal punctuation.
 * When `ensureQuestion` is true, each item is coerced into a question by
 * replacing trailing "."/"!" with "?" and appending "?" when missing.
 */
const normalizeGuideItems = (
  items: string[] | undefined,
  ensureQuestion = false,
): string[] => {
  if (!items || items.length === 0) {
    return [];
  }

  const normalized: string[] = [];
  for (const item of items) {
    const cleanedItem = item?.trim();
    if (!cleanedItem) {
      continue;
    }

    const finalValue =
      ensureQuestion && !cleanedItem.endsWith("?")
        ? `${cleanedItem.replace(/[.!]+$/, "")}?`
        : cleanedItem;

    if (!normalized.includes(finalValue)) {
      normalized.push(finalValue);
    }

    if (normalized.length >= MAX_GUIDE_ITEMS) {
      break;
    }
  }

  return normalized;
};

export const InterviewGuide = ({
  screening: { interviewGuide },
}: InterviewGuideProps) => {
  const candidateHighlights = normalizeGuideItems(
    interviewGuide?.candidateHighlights,
  );
  const potentialWeaknesses = normalizeGuideItems(
    interviewGuide?.potentialWeaknesses,
  );
  const followUpQuestions = normalizeGuideItems(
    interviewGuide?.recommendedFollowUpQuestions,
    true,
  );

  if (
    candidateHighlights.length === 0 &&
    potentialWeaknesses.length === 0 &&
    followUpQuestions.length === 0
  ) {
    return null;
  }

  return (
    <div className="mb-8 flex flex-col gap-5">
      <hr className="border-uw-gray-1" />
      <p className="text-lg font-semibold">Interview guide</p>
      <GuideBlock
        items={candidateHighlights}
        label="Candidate highlights"
        markerClassName="bg-uw-mint-green-8"
      />
      <GuideBlock
        items={potentialWeaknesses}
        label="Potential weaknesses"
        markerClassName="bg-uw-red-3"
      />
      <GuideBlock
        items={followUpQuestions}
        label="Recommended follow-up questions"
        markerClassName="bg-uw-purple-5"
      />
    </div>
  );
};
